import { c as _c } from "react/compiler-runtime";
import type { AgentVersionHistoryItem } from '@onefootprint/request-types/dashboard';
import { LoadingSpinner } from '@onefootprint/ui';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import useAgentReleases from '@/hooks/use-agent-releases';
import { useBuilder } from '../../store/provider';
import HistoryRow from './history-row';
import useAgentHistory from './use-agent-history';
type HistoryListProps = {
  tab: 'history' | 'releases';
  onSelectVersion?: () => void;
};
const HistoryList = t0 => {
  const $ = _c(40);
  const {
    tab,
    onSelectVersion
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "agentBuilder.history"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  const {
    state,
    setPreviewVersionId
  } = useBuilder();
  const agentId = state.session?.agentId;
  const history = useAgentHistory(agentId, tab === "history");
  const releases = useAgentReleases(agentId, tab === "releases");
  const source = tab === "history" ? history : releases;
  const {
    items,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage
  } = source;
  const sentinelRef = useRef(null);
  let t2;
  if ($[1] !== onSelectVersion || $[2] !== setPreviewVersionId) {
    t2 = version => {
      setPreviewVersionId(version.isHead ? null : version.id);
      onSelectVersion?.();
    };
    $[1] = onSelectVersion;
    $[2] = setPreviewVersionId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const selectVersion = t2;
  let t3;
  let t4;
  if ($[4] !== fetchNextPage || $[5] !== hasNextPage || $[6] !== isFetchingNextPage) {
    t3 = () => {
      const node = sentinelRef.current;
      if (!node || !hasNextPage || isFetchingNextPage) {
        return;
      }
      const observer = new IntersectionObserver(entries => {
        if (entries.some(_temp)) {
          fetchNextPage();
        }
      }, {
        rootMargin: "200px"
      });
      observer.observe(node);
      return () => observer.disconnect();
    };
    t4 = [hasNextPage, isFetchingNextPage, fetchNextPage];
    $[4] = fetchNextPage;
    $[5] = hasNextPage;
    $[6] = isFetchingNextPage;
    $[7] = t3;
    $[8] = t4;
  } else {
    t3 = $[7];
    t4 = $[8];
  }
  useEffect(t3, t4);
  let t5;
  if ($[9] !== t || $[10] !== tab) {
    t5 = tab === "releases" ? t(_temp2) : t(_temp3);
    $[9] = t;
    $[10] = tab;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const emptyLabel = t5;
  if (isLoading && items.length === 0) {
    let t6;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <div className="flex justify-center py-8"><LoadingSpinner /></div>;
      $[12] = t6;
    } else {
      t6 = $[12];
    }
    return t6;
  }
  if (items.length === 0 || !state.current) {
    let t6;
    if ($[13] !== emptyLabel) {
      t6 = <p className="text-label-3 text-primary">{emptyLabel}</p>;
      $[13] = emptyLabel;
      $[14] = t6;
    } else {
      t6 = $[14];
    }
    let t7;
    if ($[15] !== t || $[16] !== tab) {
      t7 = tab === "releases" ? t(_temp4) : t(_temp5);
      $[15] = t;
      $[16] = tab;
      $[17] = t7;
    } else {
      t7 = $[17];
    }
    let t8;
    if ($[18] !== t7) {
      t8 = <p className="text-body-3 text-tertiary">{t7}</p>;
      $[18] = t7;
      $[19] = t8;
    } else {
      t8 = $[19];
    }
    let t9;
    if ($[20] !== t6 || $[21] !== t8) {
      t9 = <div className="flex flex-col gap-1 p-4">{t6}{t8}</div>;
      $[20] = t6;
      $[21] = t8;
      $[22] = t9;
    } else {
      t9 = $[22];
    }
    return t9;
  }
  let t6;
  if ($[23] !== items || $[24] !== onSelectVersion || $[25] !== selectVersion || $[26] !== state.current || $[27] !== tab) {
    let t7;
    if ($[29] !== onSelectVersion || $[30] !== selectVersion || $[31] !== state.current || $[32] !== tab) {
      t7 = item => <HistoryRow key={item.id} item={item} tab={tab} current={state.current} onSelect={selected => selectVersion(selected)} onSelectVersion={onSelectVersion} />;
      $[29] = onSelectVersion;
      $[30] = selectVersion;
      $[31] = state.current;
      $[32] = tab;
      $[33] = t7;
    } else {
      t7 = $[33];
    }
    t6 = items.map(t7);
    $[23] = items;
    $[24] = onSelectVersion;
    $[25] = selectVersion;
    $[26] = state.current;
    $[27] = tab;
    $[28] = t6;
  } else {
    t6 = $[28];
  }
  let t7;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div ref={sentinelRef} aria-hidden={true} />;
    $[34] = t7;
  } else {
    t7 = $[34];
  }
  let t8;
  if ($[35] !== isFetchingNextPage) {
    t8 = isFetchingNextPage && <li className="flex justify-center py-3"><LoadingSpinner /></li>;
    $[35] = isFetchingNextPage;
    $[36] = t8;
  } else {
    t8 = $[36];
  }
  let t9;
  if ($[37] !== t6 || $[38] !== t8) {
    t9 = <ul className="flex flex-col">{t6}{t7}{t8}</ul>;
    $[37] = t6;
    $[38] = t8;
    $[39] = t9;
  } else {
    t9 = $[39];
  }
  return t9;
};
export default HistoryList;
function _temp(e) {
  return e.isIntersecting;
}
function _temp2($) {
  return $.emptyReleases;
}
function _temp3($_0) {
  return $_0.empty;
}
function _temp4($_1) {
  return $_1.emptyReleasesHint;
}
function _temp5($_2) {
  return $_2.emptyHint;
}